import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Car, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { memo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { formatTimeShort } from '@/utils/bayScheduleHelpers';
import { ScrollArea } from '@/components/ui/scroll-area';
// DMC-D3c-1: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';

/** OCT28 #4: below Tailwind's lg breakpoint (1024 px) — phones and tablets. RECOVERY: it was md (768 px), so a
 *  768 px tablet kept the panel in flow and lost 42% of its map width. */
export function isNarrowViewport(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1023px)').matches;
}

interface NotYetArrivedPanelProps {
  tickets: any[];
  selectedDate: Date;
  readOnly?: boolean;
}

// Draggable ticket card for not-yet-arrived vehicles
const DraggableNotYetArrivedCard = memo(({ ticket, readOnly }: { ticket: any; readOnly?: boolean }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `ticket:${ticket.id}`,
    disabled: readOnly,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "p-3 bg-card rounded-lg border border-border transition-all",
        readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing hover:shadow-md hover:border-primary/40",
        isDragging && "opacity-50"
      )}
    >
      <div className="space-y-2">
        {/* Vehicle info */}
        <div>
          <p className="font-semibold text-sm truncate">
            {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {ticket.customerName || ticket.customer_name}
          </p>
        </div>

        {/* RO number */}
        {ticket.ro_number && (
          <p className="text-xs text-muted-foreground">
            RO #{ticket.ro_number}
          </p>
        )}

        {/* Scheduled time */}
        {(ticket.scheduled_start_time || ticket.start_time) && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              {formatTimeShort(ticket.scheduled_start_time || ticket.start_time)}
            </span>
          </div>
        )}

        {/* Status badge */}
        <Badge variant="outline" className="text-[10px]">
          {ticket.status || 'Scheduled'}
        </Badge>
      </div>
    </div>
  );
});

DraggableNotYetArrivedCard.displayName = 'DraggableNotYetArrivedCard';

export const NotYetArrivedPanel = memo(({ tickets, selectedDate, readOnly }: NotYetArrivedPanelProps) => {
  // OCT28 #4: on a phone the 320 px panel left the map ~70 px wide, so fit-to-screen clamped to the minimum zoom and
  // most bays sat under the panel or off-screen ("scrambled"). Below md it starts collapsed and, when opened, overlays
  // the map instead of shrinking it. Bay coordinates and the map camera are untouched; desktop is unchanged.
  const [isCollapsed, setIsCollapsed] = useState(() => isNarrowViewport());

  // Show every ticket without a specific bay or parking-spot location.
  // This intentionally includes general-lot tickets (parking_lot=true with
  // no parking_spot_id) — e.g., post-completion vehicles awaiting a spot
  // pickup, "Mark vehicle present" general-lot placements, and rollover
  // Part B tickets sitting in the lot. Without this, those tickets would
  // disappear from Birdseye entirely (no bay card and no spot card show
  // them either). Completed and Cancelled tickets are still excluded so
  // the panel keeps focus on tickets that need action.
  const notYetArrivedTickets = tickets.filter(t =>
    !t.bay_id &&
    !t.parking_spot_id &&
    t.status !== 'Completed' &&
    t.status !== 'Cancelled' &&
    // DMC-D3c-1: canonical deleted-state check (both reps).
    !isTicketDeleted(t)
  );

  return (
    <div 
      className={cn(
        "h-full border-l bg-background transition-all duration-300",
        // narrow screens: an overlay on the map's right edge; md and up: the original side column
        "absolute inset-y-0 right-0 z-20 lg:relative lg:inset-auto lg:z-auto",
        isCollapsed ? "w-0 border-l-0 lg:w-12 lg:border-l" : "w-[min(20rem,85vw)] shadow-xl lg:w-80 lg:shadow-none"
      )}
      data-testid="not-yet-arrived-panel"
      data-collapsed={isCollapsed}
    >
      {/* Collapse/Expand Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-3 -left-3 z-10 h-6 w-6 rounded-full border bg-background shadow-md"
      >
        {isCollapsed ? (
          <ChevronLeft className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </Button>

      {!isCollapsed && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm">Awaiting Placement</h3>
                <p className="text-xs text-muted-foreground">
                  {notYetArrivedTickets.length} vehicle{notYetArrivedTickets.length !== 1 ? 's' : ''} (no bay or specific spot)
                </p>
              </div>
            </div>
          </div>

          {/* Ticket List */}
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-3">
              {notYetArrivedTickets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Car className="h-8 w-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    All vehicles accounted for
                  </p>
                </div>
              ) : (
                notYetArrivedTickets.map(ticket => (
                  <DraggableNotYetArrivedCard key={ticket.id} ticket={ticket} readOnly={readOnly} />
                ))
              )}
            </div>
          </ScrollArea>

          {/* Footer hint */}
          <div className="p-3 border-t bg-muted/20">
            <p className="text-[10px] text-muted-foreground text-center">
              Drag vehicles to parking spots or bays
            </p>
          </div>
        </div>
      )}

      {/* Collapsed state - show count vertically (OCT28 #4: md and up only — on phones the collapsed panel has no width) */}
      {isCollapsed && (
        <div className="hidden lg:flex flex-col items-center justify-center h-full">
          <div className="writing-mode-vertical text-xs text-muted-foreground">
            {notYetArrivedTickets.length} Not Arrived
          </div>
        </div>
      )}
    </div>
  );
});

NotYetArrivedPanel.displayName = 'NotYetArrivedPanel';
